import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore"
import { db } from "../firebaseClient"

const templatesRef = (orgId) =>
  collection(db, "organizations", orgId, "credentialTemplates")

export const DEFAULT_TEMPLATE_FIELDS = [
  { key: "learnerName", label: "Learner Name", source: "learner.name" },
  { key: "credentialTitle", label: "Credential Title", source: "template.name" },
  { key: "issueDate", label: "Issue Date", source: "date.issued" },
  { key: "credentialId", label: "Credential ID", source: "credentialId" },
  { key: "issuerName", label: "Issuer", source: "issuer.name" },
]

export async function createCredentialTemplate(orgId, data, uid) {
  try {
    const docRef = await addDoc(templatesRef(orgId), {
      name: data.name || "Untitled Template",
      type: data.type || "certificate", // certificate | badge | micro-credential | professional
      status: "draft", // draft | active | archived
      design: {
        logoUrl: data.design?.logoUrl || "",
        primaryColor: data.design?.primaryColor || "#4F46E5",
        secondaryColor: data.design?.secondaryColor || "#111827",
        backgroundColor: data.design?.backgroundColor || "#FFFFFF",
        fontFamily: data.design?.fontFamily || "Inter",
        layout: data.design?.layout || "landscape", // landscape | portrait
      },
      issuer: {
        name: data.issuer?.name || "",
        title: data.issuer?.title || "",
        signatureUrl: data.issuer?.signatureUrl || "",
      },
      fields: data.fields?.length ? data.fields : DEFAULT_TEMPLATE_FIELDS,
      skills: data.skills || [],
      evidence: {
        linkedCourseId: data.evidence?.linkedCourseId || null,
        linkedProgramId: data.evidence?.linkedProgramId || null,
        minAssessmentScore: data.evidence?.minAssessmentScore ?? null,
        requireProjectSubmission: !!data.evidence?.requireProjectSubmission,
      },
      verification: {
        publicVerification: data.verification?.publicVerification ?? true,
        showQr: data.verification?.showQr ?? true,
      },
      onChain: {
        enabled: false,
        network: null,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: uid || null,
    })
    return { id: docRef.id, error: null }
  } catch (error) {
    return { id: null, error: error.message }
  }
}

export async function getCredentialTemplates(orgId) {
  try {
    const q = query(templatesRef(orgId), orderBy("updatedAt", "desc"))
    const snap = await getDocs(q)
    return { templates: snap.docs.map((d) => ({ id: d.id, ...d.data() })), error: null }
  } catch (error) {
    return { templates: [], error: error.message }
  }
}

export async function getCredentialTemplate(orgId, templateId) {
  try {
    const snap = await getDoc(doc(db, "organizations", orgId, "credentialTemplates", templateId))
    if (!snap.exists()) return { template: null, error: "Template not found" }
    return { template: { id: snap.id, ...snap.data() }, error: null }
  } catch (error) {
    return { template: null, error: error.message }
  }
}

export async function updateCredentialTemplate(orgId, templateId, updates) {
  try {
    await updateDoc(doc(db, "organizations", orgId, "credentialTemplates", templateId), {
      ...updates,
      updatedAt: serverTimestamp(),
    })
    return { error: null }
  } catch (error) {
    return { error: error.message }
  }
}

export async function deleteCredentialTemplate(orgId, templateId) {
  try {
    await deleteDoc(doc(db, "organizations", orgId, "credentialTemplates", templateId))
    return { error: null }
  } catch (error) {
    return { error: error.message }
  }
}

export async function duplicateCredentialTemplate(orgId, template, uid) {
  const { id, createdAt, updatedAt, ...rest } = template
  return createCredentialTemplate(orgId, { ...rest, name: `${rest.name} (Copy)` }, uid)
}